/**
 * The language table shared by the editor, the runner, the AI layer and storage.
 * Pure data: no CodeMirror imports here so it can be used from unit tests and workers.
 */
export type RunnerKind = 'python' | 'javascript' | 'html';

export interface LanguageInfo {
  id: string;
  label: string;
  /** File extension used for downloads. */
  extension: string;
  /** Name in @codemirror/language-data. Null falls back to plain text. */
  codemirror: string | null;
  runner: RunnerKind | null;
}

export const LANGUAGES: readonly LanguageInfo[] = [
  { id: 'python', label: 'Python', extension: 'py', codemirror: 'Python', runner: 'python' },
  { id: 'javascript', label: 'JavaScript', extension: 'js', codemirror: 'JavaScript', runner: 'javascript' },
  { id: 'typescript', label: 'TypeScript', extension: 'ts', codemirror: 'TypeScript', runner: null },
  { id: 'html', label: 'HTML', extension: 'html', codemirror: 'HTML', runner: 'html' },
  { id: 'css', label: 'CSS', extension: 'css', codemirror: 'CSS', runner: null },
  { id: 'matlab', label: 'MATLAB', extension: 'm', codemirror: 'Octave', runner: null },
  { id: 'json', label: 'JSON', extension: 'json', codemirror: 'JSON', runner: null },
  { id: 'markdown', label: 'Markdown', extension: 'md', codemirror: 'Markdown', runner: null },
  { id: 'sql', label: 'SQL', extension: 'sql', codemirror: 'SQL', runner: null },
  { id: 'shell', label: 'Shell', extension: 'sh', codemirror: 'Shell', runner: null },
  { id: 'java', label: 'Java', extension: 'java', codemirror: 'Java', runner: null },
  { id: 'cpp', label: 'C++', extension: 'cpp', codemirror: 'C++', runner: null },
  { id: 'csharp', label: 'C#', extension: 'cs', codemirror: 'C#', runner: null },
  { id: 'go', label: 'Go', extension: 'go', codemirror: 'Go', runner: null },
  { id: 'rust', label: 'Rust', extension: 'rs', codemirror: 'Rust', runner: null },
  { id: 'php', label: 'PHP', extension: 'php', codemirror: 'PHP', runner: null },
  { id: 'ruby', label: 'Ruby', extension: 'rb', codemirror: 'Ruby', runner: null },
  { id: 'swift', label: 'Swift', extension: 'swift', codemirror: 'Swift', runner: null },
  { id: 'kotlin', label: 'Kotlin', extension: 'kt', codemirror: 'Kotlin', runner: null },
  { id: 'yaml', label: 'YAML', extension: 'yaml', codemirror: 'YAML', runner: null },
  { id: 'xml', label: 'XML', extension: 'xml', codemirror: 'XML', runner: null },
  { id: 'text', label: 'Plain text', extension: 'txt', codemirror: null, runner: null },
];

const ALIASES: Record<string, string> = {
  py: 'python',
  python3: 'python',
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  node: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  htm: 'html',
  m: 'matlab',
  octave: 'matlab',
  'c++': 'cpp',
  c: 'cpp',
  c_cpp: 'cpp',
  cc: 'cpp',
  cs: 'csharp',
  'c#': 'csharp',
  golang: 'go',
  rs: 'rust',
  rb: 'ruby',
  kt: 'kotlin',
  yml: 'yaml',
  sh: 'shell',
  bash: 'shell',
  zsh: 'shell',
  md: 'markdown',
  txt: 'text',
  plain: 'text',
  plaintext: 'text',
  '': 'text',
};

/** Map any language spelling (fence label, Ace mode id, extension) to a canonical id. Unknown values become "text". */
export function normalizeLanguage(value: string | null | undefined): string {
  const key = (value ?? '').trim().toLowerCase().replace(/^ace\/mode\//, '');
  if (LANGUAGES.some((l) => l.id === key)) return key;
  return ALIASES[key] ?? 'text';
}

export function languageInfo(id: string): LanguageInfo {
  const normalized = normalizeLanguage(id);
  return LANGUAGES.find((l) => l.id === normalized) ?? LANGUAGES[LANGUAGES.length - 1]!;
}
